import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader, XCircle } from 'lucide-react';

const PaymentSuccess = () => {
  const [paymentStatus, setPaymentStatus] = useState('checking');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [error, setError] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, checkAuthentication } = useAuth();

  // Extract session_id from URL
  const getSessionId = () => {
    const params = new URLSearchParams(location.search);
    return params.get('session_id');
  };

  // Poll payment status
  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000; // 2 seconds

    if (attempts >= maxAttempts) {
      setPaymentStatus('timeout');
      setError('Payment status check timed out. Please check your email for confirmation or contact support.');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/status/${sessionId}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to check payment status');
      }

      const data = await response.json();
      setPaymentDetails(data);
      
      if (data.payment_status === 'paid') {
        setPaymentStatus('success');
        // Refresh user data to get updated membership
        await checkAuthentication();
        return;
      } else if (data.status === 'expired') {
        setPaymentStatus('expired');
        setError('Payment session expired. Please try again.');
        return;
      } else if (data.payment_status === 'failed') {
        setPaymentStatus('failed');
        setError('Payment failed. Please try again.');
        return;
      }

      // If payment is still pending, continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setPaymentStatus('error');
      setError('Error checking payment status. Please try again.');
    }
  };

  useEffect(() => {
    const sessionId = getSessionId();
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setPaymentStatus('error');
      setError('No payment session found.');
    }
  }, []);

  const handleContinue = () => {
    if (user?.role === 'customer') {
      navigate('/profile');
    } else {
      navigate('/');
    }
  };

  const handleRetry = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4">
            {paymentStatus === 'checking' && (
              <Loader className="h-12 w-12 text-purple-600 animate-spin" />
            )}
            {paymentStatus === 'success' && (
              <CheckCircle className="h-12 w-12 text-green-600" />
            )}
            {(paymentStatus === 'failed' || paymentStatus === 'expired' || paymentStatus === 'error' || paymentStatus === 'timeout') && (
              <XCircle className="h-12 w-12 text-red-600" />
            )}
          </div>
          
          <CardTitle className="text-2xl">
            {paymentStatus === 'checking' && 'Processing Payment...'}
            {paymentStatus === 'success' && 'Payment Successful!'}
            {paymentStatus === 'failed' && 'Payment Failed'}
            {paymentStatus === 'expired' && 'Payment Expired'}
            {paymentStatus === 'error' && 'Payment Error'}
            {paymentStatus === 'timeout' && 'Status Check Timeout'}
          </CardTitle>
        </CardHeader>
        
        <CardContent className="text-center space-y-4">
          {paymentStatus === 'checking' && (
            <p className="text-gray-600">
              Please wait while we confirm your payment...
            </p>
          )}
          
          {paymentStatus === 'success' && paymentDetails && (
            <div className="space-y-3">
              <p className="text-green-600 font-medium">
                Thank you for your purchase!
              </p>
              <div className="bg-green-50 p-4 rounded-lg text-left">
                <h4 className="font-semibold text-green-800 mb-2">Payment Details:</h4>
                <p className="text-sm text-green-700">
                  <strong>Package:</strong> {paymentDetails.package?.name}
                </p>
                <p className="text-sm text-green-700">
                  <strong>Amount:</strong> ${paymentDetails.amount_total / 100} {paymentDetails.currency?.toUpperCase()}
                </p>
                <p className="text-sm text-green-700">
                  <strong>Status:</strong> Paid
                </p>
              </div>
              <p className="text-sm text-gray-600">
                Your membership has been activated. You can now access your dashboard.
              </p>
            </div>
          )}
          
          {error && (
            <div className="bg-red-50 p-4 rounded-lg">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}
          
          <div className="pt-4">
            {paymentStatus === 'success' && (
              <Button onClick={handleContinue} className="w-full bg-purple-600 hover:bg-purple-700">
                Continue to Dashboard
              </Button>
            )}
            
            {(paymentStatus === 'failed' || paymentStatus === 'expired' || paymentStatus === 'error' || paymentStatus === 'timeout') && (
              <div className="space-y-2">
                <Button onClick={handleRetry} className="w-full bg-purple-600 hover:bg-purple-700">
                  Try Again
                </Button>
                <Button 
                  onClick={() => navigate('/')} 
                  variant="outline" 
                  className="w-full"
                >
                  Return Home
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;